/**
 * Copy Trading Main Page
 *
 * 카피 트레이딩 메인 페이지
 * - 포트폴리오 랭킹 표시 (ROI, Win Rate, Balanced 등)
 * - 검색 및 필터링
 * - 포트폴리오 카드 목록
 * - Mock API 데이터 사용
 *
 * @see docs/COPY_TRADING_FRONTEND_PLAN.md
 */

import { COPY_TRADE_MENU, LEADER_TABS } from '@app/constants/QtabMenu.ts';
import { useToken } from '@app/hooks';
import { useIsTablet } from '@utils/CalVW.tsx';
import { message, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import * as S from '@app/pages/QBIT/Loan/style.ts';
import * as TS from './style.ts';

import { PortfolioCard } from './components/PortfolioCard';
import { CopyRegisterModal } from './modal/CopyRegisterModal';
import { FollowPortfolioModal } from './modal/FollowPortfolioModal';
import { WalletTransferModal } from './modal/WalletTransferModal';

import { useMyProfile } from '@/app/hooks/user/myprofile.tsx';
import type { CopyPortfolio } from '../../../types/copyTrading';
import { useCopyPortfolioStore } from '../../hooks/copyTrading/copyPortfolioStore';
import { useMyPortfolio } from '../../hooks/copyTrading/useMyPortfolio';

// Copy Trade menu routes (기본 탭)
export const getCopyTradeMenuRoutes = () => ({
  [COPY_TRADE_MENU[0]]: '/copy-trade', // Discover (탐색)
  [COPY_TRADE_MENU[1]]: '/copy-trade/following', // Following (팔로잉)
  [COPY_TRADE_MENU[2]]: '/copy-trade/orders', // Order History (주문 이력)
  [COPY_TRADE_MENU[3]]: '/copy-trade/settlements', // Settlements (정산)
  default: '/copy-trade',
});

// Leader routes (리더 전용 탭)
export const getLeaderMenuRoutes = () => ({
  [LEADER_TABS[0]]: '/copy-trade/my-portfolio', // My Portfolio (내 포트폴리오)
  [LEADER_TABS[1]]: '/copy-trade/my-followers', // My Followers (내 팔로워)
  [LEADER_TABS[2]]: '/copy-trade/earnings', // Earnings (수익)
});

// 호환성을 위한 기존 변수
export const copyTradeMenuRoutes = getCopyTradeMenuRoutes();

export function CopyTradePage() {
  const { t } = useTranslation();
  const { token } = useToken();

  const [filteredPortfolios, setFilteredPortfolios] = useState<CopyPortfolio[]>(
    []
  );
  const [openRegisterModal, setOpenRegisterModal] = useState(false);
  const [openFollowModal, setOpenFollowModal] = useState(false);
  const [openWalletTransferModal, setOpenWalletTransferModal] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] =
    useState<CopyPortfolio | null>(null);

  // Filter & Sort State
  const [sortBy, setSortBy] = useState<
    'roi30d' | 'winRate' | 'sharpeRatio' | 'all'
  >('all');
  const [searchKeyword, setSearchKeyword] = useState('');

  // 포트폴리오 조회 (리더 탭 표시 조건)
  const { portfolio: myPortfolio, hasPortfolio } = useMyPortfolio();
  const { data: myProfile } = useMyProfile();

  // Zustand Store
  const {
    portfolios,
    portfolioStatsMap,
    myFollows,
    fetchPortfolios,
    fetchMyFollows,
    isLoading,
    error,
    clearError,
  } = useCopyPortfolioStore();

  // ==========================================
  // Effects
  // ==========================================

  // Initial data fetch
  useEffect(() => {
    fetchPortfolios({ status: 1, page: 1, pageSize: 20 });

    // 로그인 상태일 때만 내 팔로우 목록 로드
    if (token) {
      fetchMyFollows();
    }
  }, [token]);

  // Error handling
  useEffect(() => {
    if (error) {
      message.error(error);
      clearError();
    }
  }, [error]);

  // ==========================================
  // Handlers
  // ==========================================

  const handleCopyPortfolio = (portfolio: CopyPortfolio) => {
    setSelectedPortfolio(portfolio);
    setOpenFollowModal(true);
  };

  const handleSortChange = (
    sortType: 'roi30d' | 'winRate' | 'sharpeRatio' | 'all'
  ) => {
    setSortBy(sortType);

    if (sortType === 'all') {
      fetchPortfolios({ status: 1, page: 1, pageSize: 20 });
    } else {
      fetchPortfolios({
        status: 1,
        sortBy: (sortType === 'roi30d' ? 'pnl' : sortType) as any,
        order: 'desc',
        page: 1,
        pageSize: 20,
      });
    }
  };

  useEffect(() => {
    if (!portfolios) return;
    const filtered = portfolios.filter((portfolio) => {
      return (
        portfolio.leaderUsername
          .toLowerCase()
          .includes(searchKeyword.toLowerCase()) ||
        portfolio.portfolioName
          .toLowerCase()
          .includes(searchKeyword.toLowerCase())
      );
    });

    setFilteredPortfolios(filtered);
  }, [portfolios, searchKeyword]);

  return (
    <>
      {/* Modals */}
      <CopyRegisterModal
        open={openRegisterModal}
        setOpen={setOpenRegisterModal}
      />
      <FollowPortfolioModal
        open={openFollowModal}
        setOpen={setOpenFollowModal}
        portfolio={selectedPortfolio}
      />
      <WalletTransferModal
        open={openWalletTransferModal}
        setOpen={setOpenWalletTransferModal}
      />

      <>
        {/* Header Section */}
        <S.SectionHeaderContainer>
          <S.SectionHeaderTextWrap>
            <S.SectionHeaderTitle>{t('copyTradingTitle')}</S.SectionHeaderTitle>
            <S.SectionHeaderDesc>
              {t('copyTradingSubtitle')}
              {'\n'}
              {t('copyTradingDesc')}
            </S.SectionHeaderDesc>

            {/* Action Buttons - 헤더 바로 아래 */}
            {token && (
              <div
                style={{
                  marginTop: '16px',
                  marginBottom: '8px',
                  display: 'flex',
                  gap: '12px',
                }}
              >
                {/* Wallet Transfer Button - 항상 표시 */}
                <button
                  onClick={() => setOpenWalletTransferModal(true)}
                  style={{
                    padding: '10px 20px',
                    background:
                      'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow =
                      '0 4px 12px rgba(59, 130, 246, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 2px 8px rgba(59, 130, 246, 0.3)';
                  }}
                >
                  💰 {t('transfer')}
                </button>

                {/* Leader Registration Button - 포트폴리오 없을 때만 표시 */}
                {!hasPortfolio && (
                  <button
                    onClick={() => setOpenRegisterModal(true)}
                    style={{
                      padding: '10px 20px',
                      background:
                        'linear-gradient(135deg, #FF9131 0%, #FF6B00 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(255, 145, 49, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow =
                        '0 4px 12px rgba(255, 145, 49, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow =
                        '0 2px 8px rgba(255, 145, 49, 0.3)';
                    }}
                  >
                    {t('becomeALeader')}
                  </button>
                )}
              </div>
            )}
          </S.SectionHeaderTextWrap>
        </S.SectionHeaderContainer>

        {/* Loading State */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        )}

        {!isLoading && (
          <>
            {/* All Portfolios Section */}
            <TS.BottomContainer>
              <TS.BottomHeaderWrap>
                <TS.BottomHeaderTitle>{t('allTraders')}</TS.BottomHeaderTitle>
                <TS.StyledSearch
                  onSearch={(value) => setSearchKeyword(value)}
                  placeholder={
                    t('searchTradersPlaceholder') || 'Search traders...'
                  }
                  allowClear
                  size="large"
                />
              </TS.BottomHeaderWrap>

              {/* 반응형 그리드 - 자동으로 줄바꿈 */}
              <TS.BottomContentWrap>
                {filteredPortfolios.map((portfolio) => {
                  // 이미 팔로우 중인지 확인
                  const isFollowing = (myFollows || []).some(
                    (f) =>
                      // f.follow.portfolioId === portfolio.id &&
                      // f.follow.status !== 3 // 종료된 팔로우는 제외 현재는 오류로 인해 주석 처리
                      f.follow.portfolioId === portfolio.id
                  );
                  const isMyPortfolio =
                    myProfile?.email === portfolio.leaderUsername ||
                    myProfile?.phone === portfolio.leaderUsername;

                  return (
                    <PortfolioCard
                      key={portfolio.id}
                      portfolio={portfolio}
                      stats={portfolioStatsMap[portfolio.id]}
                      onCopy={handleCopyPortfolio}
                      isFollowing={isFollowing}
                      isMyPortfolio={isMyPortfolio}
                    />
                  );
                })}
              </TS.BottomContentWrap>
            </TS.BottomContainer>
          </>
        )}
      </>
    </>
  );
}
